import React, { useState } from 'react';

import CompareCheckbox from '../../../../components/compare-checkbox/compare-checkbox';
import FoundItLowerButton from '../../../../components/found-it-lower-button/found-it-lower-button';
import ProductArticleStatusBanner from '../../../../components/product-article-status-banner/product-article-status-banner';
import ProductAvailability from '../../../../components/product-availability/product-availability';
import ProductContainsDescription from '../../../../components/product-contains-description/product-contains-description';
import ProductDescription from '../../../../components/product-description/product-description';
import ProductFirstPriorityDetails from '../../../../components/product-first-priority-details/product-first-priority-details';
import ProductImage from '../../../../components/product-image/product-image';
import ProductOECompatible from '../../../../components/product-oe-compatible/product-oe-compatible';
import ProductRating from '../../../../components/product-rating/product-rating';
import ProductSwatchSelection from '../../../../components/product-swatch-selection/product-swatch-selection';
import ProductWarranty from '../../../../components/product-warranty/product-warranty';
import TireAttributeIcons from '../../../../components/tire-attribute-icons/tire-attribute-icons';
import TpmsAccessoryMessage from '../../../../components/tpms-accessory-message/tpms-accessory-message';
import ViewOnVehicleButton from '../../../../components/view-on-vehicle-button/view-on-vehicle-button';
import ProductListCardPrice from '../product-list-card-price/product-list-card-price';

import {
  useMediaQuery,
  useOptimisticData,
  useProductEntryDataLayerValues,
  useProductSwatchSelection
} from '../../../../shared/hooks/hooks';

import { trackAction } from '../../../../shared/utils/analytics/analytics';
import { updatePageNumOnBackToResultsUrl } from '../../../../shared/utils/back-to-results/back-to-results';
import { getShouldShowCompare } from '../../../../shared/utils/block-utils/block-utils';
import {
  getAverageReviewRating,
  getNormalizedProductType,
  getNumberOfProductReviews,
  getPrioritizedArticleStatusProduct,
  getProductWarranty,
  getShouldShowProductArticleStatusBanner,
  getTireAttributeIcons,
  isCanonicalProduct
} from '../../../../shared/utils/product/product';

import { ProductData } from '../../../../shared/types/graphql-types';
import { BACK_TO_RESULTS_PAGE_IDS } from '../../../../shared/constants/back-to-results';
import {
  ACCESSORY_TYPES,
  PRODUCT_TYPES,
  TIRE_ATTIBUTES
} from '../../../../shared/constants/products';

import './product-list-card-details.scss';

interface ProductListCardDetailsProps {
  className?: string;
  hideAvailability?: boolean;
  hideCompare?: boolean;
  hideFoundItLower?: boolean;
  hidePrice?: boolean;
  hideProductCode?: boolean;
  hideProductSize?: boolean;
  hideReviews?: boolean;
  onCheckInventoryClick: () => void;
  onViewProductClick: () => void;
  pageNum?: number;
  product: ProductData;
  productDetailUrl: string;
  quantity: number;
  rearQuantity?: number;
  showPriceInDescription?: boolean;
  showProductContainsDescription?: boolean;
  totalQuantity: number;
}

function ProductListCardDetails(props: ProductListCardDetailsProps) {
  const {
    className = '',
    hideAvailability,
    hideCompare,
    hideFoundItLower,
    hideProductCode = false,
    hideProductSize = false,
    hideReviews,
    hidePrice = false,
    onCheckInventoryClick,
    onViewProductClick,
    pageNum,
    product,
    productDetailUrl,
    quantity,
    rearQuantity,
    showPriceInDescription,
    showProductContainsDescription = false,
    totalQuantity
  } = props;

  const {
    accessoryType,
    articleStatus,
    brand,
    name,
    code,
    gbb,
    origEquipmentData,
    rearProduct,
    swatches,
    unitOfMeasureDescription,
    vendorFinish
  } = product || {};

  const optimisticData = useOptimisticData();
  const isCanonical = isCanonicalProduct(product);
  const isStaggered = Boolean(rearProduct);
  const size = isCanonicalProduct(product) ? undefined : product?.size;
  const productType = getNormalizedProductType(product);

  const logoUrl = product?.brandLogo?.url;
  const numberOfProductReview = getNumberOfProductReviews(product);
  const averageReviewRating = getAverageReviewRating(product);
  const shouldShowCompare = getShouldShowCompare(articleStatus);
  const { rank } = product?.recommendationData || {};
  const breakpoint = useMediaQuery();

  const { selectedSwatch, saveSelectedSwatch } = useProductSwatchSelection(
    code,
    swatches
  );

  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();

  const [imageUrl, setImageUrl] = useState(selectedSwatch?.linkUrl ?? '');
  const [productCode, setProductCode] = useState(code);

  const isOriginalEquipment = Boolean(origEquipmentData);
  const articleStatusProduct = getPrioritizedArticleStatusProduct(
    product,
    product?.rearProduct
  );
  const shouldShowProductArticleStatusBanner =
    getShouldShowProductArticleStatusBanner(articleStatusProduct);

  const treadLifeMedian = product?.recommendationData?.mileage?.median;
  const treadLifeMin = product?.recommendationData?.mileage?.min;

  function setOptimisticProductData() {
    optimisticData.write({ product });
    onViewProductClick?.();
  }

  function onSwatchChange(swatch) {
    setImageUrl(swatch?.linkUrl);
    saveSelectedSwatch(swatch);
    setProductCode(swatch?.productCode);
  }

  function handleReadReviewsClick() {
    if (process.env.__FF_LOAD_MORE__ === 'true') {
      updatePageNumOnBackToResultsUrl(BACK_TO_RESULTS_PAGE_IDS.PLP, pageNum);
    }
    trackAction(
      'product_read_reviews_click',
      getProductEntryDataLayerValues(product)
    );
  }

  return (
    <>
      <div
        className={`${className} product-list-card-details`}
        styleName="container"
      >
        <div styleName="main">
          <div
            className="product-list-card-details__img-col"
            styleName="img-col"
          >
            <ProductImage
              imageUrl={imageUrl}
              linkUrl={productDetailUrl}
              onClick={setOptimisticProductData}
              product={product}
              styleName={
                productType === PRODUCT_TYPES.TIRES ? 'tire-img' : 'product-img'
              }
              width={breakpoint.isMinMd ? 160 : 120}
            />
            {!isStaggered && (
              <>
                {!hidePrice && !showPriceInDescription && (
                  <div className="hide-md">
                    <ProductListCardPrice
                      product={product}
                      quantity={quantity}
                      totalQuantity={totalQuantity}
                    />
                  </div>
                )}
                {!hideFoundItLower && (
                  <FoundItLowerButton
                    className="hide-md"
                    productItem={{
                      product: product,
                      quantity: quantity,
                      totalQuantity: totalQuantity
                    }}
                  />
                )}
              </>
            )}

            {shouldShowCompare && (
              <div>
                {!hideCompare &&
                  !isCanonical &&
                  productType === PRODUCT_TYPES.TIRES && (
                    <CompareCheckbox
                      pageNum={pageNum}
                      product={product}
                      quantity={quantity}
                      rearQuantity={rearQuantity}
                    />
                  )}
              </div>
            )}
          </div>

          <div
            className="product-list-card-details__desc-col"
            styleName="desc-col"
          >
            <ProductDescription
              brand={brand}
              brandLogoUrl={logoUrl}
              code={
                isCanonical || isStaggered || hideProductCode
                  ? undefined
                  : productCode
              }
              gbb={gbb}
              isOE={isOriginalEquipment}
              link={productDetailUrl}
              name={name}
              onClick={setOptimisticProductData}
              productType={productType}
              showGBBTooltip
              size={isStaggered || hideProductSize ? undefined : size}
              treadwellRank={rank}
            />

            {!hideReviews && productType === PRODUCT_TYPES.TIRES && (
              <ProductRating
                avgReviewRating={averageReviewRating}
                className="hide-md"
                link={`${productDetailUrl}#read-reviews`}
                numberOfReviews={numberOfProductReview}
                onReadReviewClick={handleReadReviewsClick}
                productCode={product?.code}
                rearProductCode={rearProduct?.code}
              />
            )}

            <ProductSwatchSelection
              onSwatchChange={onSwatchChange}
              productCode={productCode}
              productFinish={vendorFinish}
              swatches={swatches}
            />

            {!hidePrice && showPriceInDescription && (
              <ProductListCardPrice
                product={product}
                quantity={quantity}
                totalQuantity={totalQuantity}
              />
            )}

            {!treadLifeMedian && (
              <ProductWarranty
                className="hide-md"
                miles={getProductWarranty(product)}
                styleName="warranty"
              />
            )}

            {accessoryType === ACCESSORY_TYPES.TPMS_SENSOR && (
              <TpmsAccessoryMessage styleName="tpms-accessory-message" />
            )}

            {showProductContainsDescription &&
              productType === PRODUCT_TYPES.ACCESSORIES &&
              unitOfMeasureDescription && (
                <div>
                  <ProductContainsDescription product={product} />
                </div>
              )}
          </div>
        </div>
        <div styleName="secondary">
          {!hideReviews && productType === PRODUCT_TYPES.TIRES && (
            <ProductRating
              avgReviewRating={averageReviewRating}
              className="display-md"
              link={`${productDetailUrl}#read-reviews`}
              numberOfReviews={numberOfProductReview}
              onReadReviewClick={handleReadReviewsClick}
              productCode={product?.code}
            />
          )}

          {!treadLifeMedian && (
            <ProductWarranty
              className="display-md"
              miles={getProductWarranty(product)}
            />
          )}

          {product?.['isOtherVehiclesOE'] && !breakpoint.isMinMd && (
            <ProductOECompatible />
          )}

          {!hideAvailability && (
            <div styleName="secondary-section">
              {shouldShowProductArticleStatusBanner ? (
                <ProductArticleStatusBanner
                  className="hide-xxs"
                  product={articleStatusProduct}
                />
              ) : (
                <ProductAvailability
                  onCheckInventoryClick={onCheckInventoryClick}
                  product={product}
                  quantity={quantity}
                  rearProduct={product?.rearProduct}
                  rearQuantity={rearQuantity}
                />
              )}
            </div>
          )}

          {product?.isViewableOnVehicle && (
            <div styleName="secondary-section">
              <ViewOnVehicleButton
                isCanonical={isCanonical}
                onClick={() =>
                  trackAction(
                    'product_list_view_on_vehicle',
                    getProductEntryDataLayerValues(product)
                  )
                }
                product={product}
                selectedSwatch={selectedSwatch}
              />
            </div>
          )}

          {productType === PRODUCT_TYPES.TIRES && (
            <div styleName="secondary-section">
              <TireAttributeIcons
                icons={[
                  ...getTireAttributeIcons(product),
                  ...(Number(
                    product?.recommendationData?.stopping
                      ?.rollingResistanceRating
                  ) >= 4
                    ? [TIRE_ATTIBUTES.TREADWELL_FUEL_EFFICIENT]
                    : [])
                ]}
              />
            </div>
          )}

          {product.recommendationData && (
            <div styleName="secondary-section">
              <ProductFirstPriorityDetails
                dataReportUrl={productDetailUrl}
                medianMileage={treadLifeMedian}
                minMileage={treadLifeMin}
                onClick={() => {
                  trackAction(
                    'product_view_pdl_data_click',
                    getProductEntryDataLayerValues(product)
                  );
                  if (process.env.__FF_LOAD_MORE__ === 'true') {
                    updatePageNumOnBackToResultsUrl(
                      BACK_TO_RESULTS_PAGE_IDS.PLP,
                      pageNum
                    );
                  }
                }}
                product={product}
              />
            </div>
          )}
        </div>
      </div>
      {product?.['isOtherVehiclesOE'] && breakpoint.isMinMd && (
        <ProductOECompatible />
      )}
    </>
  );
}

export default ProductListCardDetails;
